import React, { createContext, useCallback, useContext, useRef } from 'react';

import { useNotificationGetList, useNotificationMarkAsRead, type Notification } from '@/entities/notification';

import { useBatchedDeactivateNotification } from 'features/notification/deactivate';

import { NOTIFICATIONS_POLLING_INTERVAL } from './constants';
import { AutoDisplay, MarkAsOld } from './plugins';
import { useNotifications } from './use-notifications';
import { useUrgentNotification } from './use-urgent-notification';
import { useNotificationsManager, type NotificationType } from './use-notifications-manager';
import { useFilterNotification } from 'features/notification/filter';

interface DrawerState {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
}

interface NotificationsContextType {
  // Data
  activeNotification: Notification | null;
  allNotifications: Notification[];
  isNotificationsLoading: boolean;
  hasUnreadNotifications: boolean;
  notificationType: NotificationType;
  urgentNotification: Notification | null;
  urgentNotifications: Notification[];
  // Drawer state
  drawer: DrawerState;

  // Actions
  openAllNotifications: () => void;
  openUrgentNotifications: (notification: Notification) => void;
  closeNotifications: () => void;
  registerTrigger: (element: HTMLElement | null) => void;

  // Batch actions
  deactivateNotification: (id: string) => void;
  markAsRead: (id: string) => void;
}

const NotificationsContext = createContext<NotificationsContextType | null>(null);

interface NotificationsProviderProps {
  children: React.ReactNode;
}

export const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const triggerElementRef = useRef<HTMLElement | null>(null);

  const {
    activeNotification,
    isOpen: isDrawerOpen,
    notificationType,
    openUrgentNotifications,
    openAllNotifications,
    closeNotifications
  } = useNotificationsManager();

  const [markAsReadQuery] = useNotificationMarkAsRead();
  const { deactivateNotification, transformQueryData } = useBatchedDeactivateNotification();
  const { filterNotification } = useFilterNotification();

  const { data, isLoading: isNotificationsLoading } = useNotificationGetList({
    refetchInterval: NOTIFICATIONS_POLLING_INTERVAL,
    select: data => transformQueryData(filterNotification(data))
  });

  const { allNotifications, hasUnreadNotifications, urgentNotifications } = useNotifications(data);

  const { urgentNotification } = useUrgentNotification(allNotifications);

  const registerTrigger = useCallback((element: HTMLElement | null) => {
    triggerElementRef.current = element;
  }, []);

  const markAsRead = useCallback(
    (id: string) => {
      markAsReadQuery([id]);
    },
    [markAsReadQuery]
  );

  const drawerState: DrawerState = {
    isOpen: isDrawerOpen,
    anchorEl: isDrawerOpen ? triggerElementRef.current?.parentElement || document.body : null
  };

  const contextValue: NotificationsContextType = {
    // Data
    activeNotification,
    allNotifications,
    isNotificationsLoading,
    hasUnreadNotifications,
    notificationType,
    urgentNotification,
    urgentNotifications,
    // Drawer state
    drawer: drawerState,

    // Actions
    openAllNotifications,
    openUrgentNotifications,
    closeNotifications,
    registerTrigger,

    // Batch actions
    deactivateNotification,
    markAsRead
  };

  return (
    <NotificationsContext.Provider value={contextValue}>
      {/** plugins */}
      <AutoDisplay autoHideDisabled={notificationType === 'all'} />
      <MarkAsOld />

      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsCtx = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotificationsCtx must be used within a NotificationsProvider');
  }
  return context;
};
